import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Constants from '../../utils/Constants';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const ERR_CODE_RESPONSE = 'ERR_BAD_RESPONSE';
const ERR_CODE_REQUEST = 'ERR_BAD_REQUEST';
const ERR_STATUS_500 = 500;
const ERR_STATUS_401 = 401;
const ERR_JWT_MESSAGE = 'JWT';
const defaultUserContext = {};
export const UserContext = createContext(defaultUserContext);

export const UserContextProvider = (props) => {
    const token = sessionStorage.getItem(Constants.LOCAL_STORAGE.AUTHORIZATION) || '';
    const [isAuthenticated, setIsAuthenticated] = useState(token.length > 0);
    const [contextStatus, setContextStatus] = useState(Constants.CONTEXT_STATUS.IDLE);
    const [role, setRole] = useState([]);
    const [userData, setUserData] = useState({});
    const [userLoginContext, setUserLoginContext] = useState({});
    const [companyList, setCompanyList] = useState({});
    const [selectedCompany, setSelectedCompany] = useState({});
    const [invitedUser, setInvitedUser] = useState({});
    const [companyManager, setCompanyManager] = useState(null);
    const navigate = useNavigate();

    const location = useLocation();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        let token = searchParams.get('token');
        if (token) {
            sessionStorage.setItem(Constants.LOCAL_STORAGE.AUTHORIZATION, token);
            setIsAuthenticated(true);
        }
    }, [searchParams, setIsAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            const getUserInfo = async () => {
                try {
                    setContextStatus(Constants.CONTEXT_STATUS.LOADING);
                    const resultPromise = await axios.get(`getUserFromToken?token=${token.slice(7)}`).catch((err) => {
                        if ((err.code === ERR_CODE_RESPONSE && err.response.status === ERR_STATUS_500 && err.response.data.message.includes(ERR_JWT_MESSAGE)) || (err.code === ERR_CODE_REQUEST && err.response.status === ERR_STATUS_401)) {
                            // In case of invalid Authorization Code, then removing the code from browser and force navigating to Login Page
                            setContextStatus(Constants.CONTEXT_STATUS.FAILED);
                            sessionStorage.removeItem(Constants.LOCAL_STORAGE.AUTHORIZATION);
                            sessionStorage.removeItem(Constants.LOCAL_STORAGE.LANGUAGE);
                            setIsAuthenticated(false);
                            setRole([]);
                            setUserData({});
                            navigate('/');
                            setInvitedUser({});
                        }
                    });
                    let userInfo = resultPromise.data;
                    setUserData(userInfo.user);
                    setRole(userInfo.role);
                    setContextStatus(Constants.CONTEXT_STATUS.SUCCESS);
                    const companyIds = userInfo.user.companyList?.map((company) => {
                        const result = company.split('-');
                        return result[0];
                    });
                    companyIds.unshift(userInfo.user.companyId);
                    if (companyIds) {
                        axios.get(`company/list?ids=${companyIds}`).then((res) => {
                            if (res.data) {
                                const companyList = res.data.map((company) => {
                                    return {
                                        companyId: company.id,
                                        companyName: company.name,
                                        serviceType: company.serviceType,
                                        logoUrl: company.logoUrl,
                                        homePage: company.homePage,
                                        phone: company.phone,
                                        region: company.region,
                                        address: company.address,
                                        memo: company.memo,
                                        emailManager: company.emailManager
                                    };
                                });
                                setCompanyList(companyList.reverse());
                                let manageCompany;
                                if (sessionStorage.getItem(Constants.LOCAL_STORAGE.SELECTED_COMPANY)) manageCompany = companyList.filter((company) => company.companyId === sessionStorage.getItem(Constants.LOCAL_STORAGE.SELECTED_COMPANY));
                                if (typeof manageCompany === 'undefined' || manageCompany.length <= 0) manageCompany = companyList.filter((company) => company.companyId === userInfo.user.companyId);
                                setSelectedCompany(manageCompany[0]);
                                setCompanyManager(manageCompany[0]);
                                axios.get(`member_invite/employeeRole?companyId=${manageCompany[0].companyId}`).then((value) => {
                                    value.data && setInvitedUser(value.data);
                                    setContextStatus(Constants.CONTEXT_STATUS.SUCCESS);
                                });
                            }
                        });
                    } else {
                        axios.get(`member_invite/employeeRole?companyId=${userInfo.user.companyId}`).then((value) => {
                            value.data && setInvitedUser(value.data);
                            setContextStatus(Constants.CONTEXT_STATUS.SUCCESS);
                        });
                    }

                    /*setRole(["SUPER_ADMIN", "VIEWER"]);*/
                } catch (err) {
                    setContextStatus(Constants.CONTEXT_STATUS.FAILED);
                    console.log('Error: ' + err.message);
                }
            };
            getUserInfo();
        }
    }, [isAuthenticated, token]);

    const isSuperAdminRole = () => {
        return role.includes(Constants.ROLE.SUPER_ADMIN);
    };

    const isManagerUser = useCallback(() => {
        if (selectedCompany && userData) {
            return selectedCompany.companyId === userData.companyId;
        }
        return false;
    }, [selectedCompany, userData]);

    const isInvitedHrRole = () => {
        // return invitedUser.includes(Constants.ROLE.INVITED_HR);
    };

    const isInvitedJobPostingRole = () => {
        // return invitedUser.includes(Constants.ROLE.INVITED_JOB);
    };

    const isUserRole = () => {
        return role.includes(Constants.ROLE.USER);
    };

    const isInvitedUserRole = () => {
        return invitedUser;
    };

    const isInvitedShowOrganizationRole = () => {
        return Object.keys(invitedUser).includes(Constants.ROLE.SHOW_ORGANIZATION);
    };

    const isCorporateCustomerRole = () => {
        return role.includes(Constants.ROLE.CORPORATE_CUSTOMER);
    };

    const permissionHandlePageAuthority = () => {
        return isCorporateCustomerRole() || isSuperAdminRole() || isManagerUser();
    };

    return (
        <UserContext.Provider
            value={{
                isAuthenticated,
                setIsAuthenticated,
                role,
                setRole,
                userData,
                setUserData,
                userLoginContext,
                setUserLoginContext,
                setInvitedUser,
                contextStatus,
                setContextStatus,
                companyList,
                setCompanyList,
                selectedCompany,
                companyManager,
                setSelectedCompany,
                isSuperAdminRole,
                isInvitedJobPostingRole,
                isInvitedHrRole,
                isUserRole,
                isInvitedUserRole,
                isCorporateCustomerRole,
                isManagerUser,
                permissionHandlePageAuthority,
                isInvitedShowOrganizationRole
            }}
        >
            {props.children}
        </UserContext.Provider>
    );
};
